import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthPageLayout from '../components/AuthPageLayout';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword({ email });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthPageLayout
        title="Check your email"
        description={
          <>
            If an account exists for <strong className="text-graphite">{email}</strong>, we've sent a password reset link. The link will expire in 1 hour.
          </>
        }
        footer={<Link to="/login" className="text-brand hover:text-brand-dark font-medium">Back to sign in</Link>}
      >
        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-state-success" />
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Reset your password"
      description="Enter the email address you use to sign in and we'll send you a link to reset your password."
      error={error}
      footer={<Link to="/login" className="text-brand hover:text-brand-dark font-medium">Back to sign in</Link>}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-graphite mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="navaris-input w-full px-4 py-3 border border-softgrey rounded-xl text-graphite placeholder-coolslate focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            placeholder="you@example.com"
            required
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="navaris-btn-primary w-full py-3 bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            'Send reset link'
          )}
        </button>
      </form>
    </AuthPageLayout>
  );
}
